import { Moment } from 'moment';
// Types
import { GetAuditLogsAdmin_getAuditLogsAdmin_items } from 'api/auditLogs/types/GetAuditLogsAdmin';
import {
  AuditLogType,
  GetAuditLogsAdminInput,
  SortDirection,
} from 'api/graphql-global-types';
import { TableColumnFilterItem, TableFilter } from 'ui/Table';
// Helpers
import { getEndOfDayDate, getStartOfDayDate } from 'helpers/utils';

type AuditLogEntry = GetAuditLogsAdmin_getAuditLogsAdmin_items;

export const getAction = (record: AuditLogEntry): string => {
  switch (record.__typename) {
    case 'AuditLogInterview':
      return record.interviewAction;
    case 'AuditLogVideoEditRequest':
      return record.videoEditRequestAction;
    default:
      return record.action;
  }
};

// Snapshot columns are empty on interview/video edit rows, so fall back to the live user.
export const getPerformer = (
  record: AuditLogEntry
): { email?: string; name?: string; role?: string } => {
  const user = record.performedBy;
  const userName = user
    ? [user.firstName, user.lastName].filter(Boolean).join(' ')
    : '';
  return {
    email: record.performedByEmail || user?.email || undefined,
    name: record.performedByName || userName || undefined,
    role: record.performedByRole || user?.role || undefined,
  };
};

export const actionGroup = (
  title: string,
  values: string[]
): TableColumnFilterItem => ({
  text: title,
  value: title,
  title,
  subItems: values.map((value) => ({ text: value, value })),
});

// The table appends a new entry per search, so the last one is the current value.
export const getTargetIdSearch = (searchFilters: TableFilter[]): string => {
  const value = [...searchFilters]
    .reverse()
    .find(({ key }) => key === 'targetId')?.value;
  return typeof value === 'string' ? value.trim() : '';
};

export const buildAuditLogsInput = ({
  searchFilters,
  direction,
  limit,
  startAfter,
  performedByEmail,
}: {
  searchFilters: TableFilter[];
  direction: SortDirection;
  limit: number;
  startAfter?: string;
  performedByEmail?: string;
}): GetAuditLogsAdminInput => {
  const input: GetAuditLogsAdminInput = { direction, limit, startAfter };

  searchFilters.forEach(({ key, value }) => {
    if (key === 'createdAt') {
      const [from, to] = (
        Array.isArray(value) ? value : [value, value]
      ) as (Moment | null)[];
      input.from = from ? getStartOfDayDate(from) : undefined;
      input.to = to ? getEndOfDayDate(to) : undefined;
    } else if (key === 'types') {
      input.types = (Array.isArray(value) ? value : [value]) as AuditLogType[];
    } else if (key === 'actions') {
      input.actions = (Array.isArray(value) ? value : [value]) as string[];
    } else if (key === 'performedById') {
      input.performedById = value as string;
    }
  });

  const targetId = getTargetIdSearch(searchFilters);
  if (targetId) {
    input.targetId = targetId;
  }

  if (performedByEmail) {
    input.performedByEmail = performedByEmail;
  }

  return input;
};
